import type { CategoryPrompt } from "../types";

import React from "react";

import {
  BarChart3,
  BatteryLow,
  CalendarDays,
  CircleDot,
  ClipboardList,
  FlaskConical,
  MessageCircle,
  Moon,
} from "lucide-react";

export const healthWellnessPrompts: CategoryPrompt[] = [
  {
    icon: <Moon />,
    title: "Problem-First Landing Pages",
    description:
      "Create a page leading with a common health problem, explaining the science, showing ingredient research, and featuring customer testimonials.",
    prompt: `**Build a problem-first landing page that leads with a specific health issue, educates on the science, and positions your product as the researched solution.**

**HEALTH PROBLEM:** e.g., "Can't Sleep Through the Night?" OR "Constant Joint Pain?" OR "Brain Fog Ruining Your Day?" OR "Digestive Issues After Every Meal?"

**YOUR SOLUTION:** e.g., "Our sleep support formula" OR "Joint relief supplement"

**Target Audience:** Choose one:
- [ ] Problem sufferers (actively dealing with issue)
- [ ] Solution seekers (tried everything, frustrated)
- [ ] Health-conscious preventers (want to avoid worsening)

**Tone & Style:**
- Empathetic to suffering
- Science-backed and credible
- Educational not salesy
- Hope and relief-focused

**CTA Guidelines:** Each CTA should:
- Reference problem solved ("End Sleepless Nights")
- Use outcome language ("Wake Refreshed")
- Include science proof ("Clinically Studied")
- Emphasize safety ("Natural Solution")

**Conversion Tactics Use:**
- Problem agitation (make them feel the pain)
- Root cause scientific explanation
- Clinical study references
- Third-party testing results
- Doctor/expert endorsements
- Customer success stories from same problem

**Key Elements to Include:**
- **Hero Section:** Problem headline as question, relatable image, primary benefit, "Get Relief" CTA
- **The Problem (2-4 paragraphs):** Detailed description of suffering and impact on life
- **You're Not Alone:** Statistics on how many people suffer from this
- **Root Cause Science:** Why this problem happens (biological/physiological explanation)
- **Why Traditional Solutions Fail:** Common treatments and their limitations
- **The Solution:** Your product introduction with key mechanism
- **How It Works:** Ingredient breakdown with scientific action for problem
- **Key Ingredients (3-5):**
  - Ingredient name + dosage
  - What it does specifically for this problem
  - Clinical research backing
  - Synergistic effects with other ingredients
- **Clinical Evidence:** Studies showing efficacy for this condition
- **Third-Party Testing:** COA, purity verification, potency confirmation
- **Dosage & Timing:** How to take for optimal results
- **Expected Timeline:** When to feel improvement (realistic expectations)
- **Expert Endorsements:** Doctor, naturopath, or specialist quote
- **Customer Testimonials (5-8):** Real people who solved this exact problem with specifics
- **Before/After Stories:** Quality of life improvements detailed
- **Safety Profile:** Side effects, interactions, who shouldn't take
- **Comparison Chart:** Your solution vs. alternatives (OTC, prescription, other supplements)
- **Money-Back Guarantee:** Risk-free trial for this problem
- **CTAs:** Hero, after science, after testimonials, with guarantee (5-6 total)
- **FAQ:** 6-8 questions (Safe? Works for everyone? How long? Side effects? Interactions? Dose? Prescription needed?)

**Goal:** Convert problem-suffering shoppers into confident buyers by deeply validating their struggle, explaining the scientific root cause, and demonstrating how your researched formula specifically addresses their issue with clinical backing and real success stories.`,
  },
  {
    icon: <BatteryLow />,
    title: "Symptom Listicle Advertorials",
    description:
      "Build a listicle addressing common symptoms, with each linking to my solution with scientific explanations and CTAs.",
    prompt: `**Build a symptom-focused listicle that identifies common health signals and positions your product as the scientifically-backed solution.**

**TOPIC:** e.g., "7 Signs Your Body Is Desperately Lacking Magnesium" OR "5 Symptoms of Chronic Inflammation You're Ignoring" OR "Why You're Always Tired: 8 Hidden Deficiencies"

**YOUR SOLUTION:** e.g., "Our magnesium complex solves symptom #3" OR "Our inflammation support blend"

**Reader Type:** Choose one:
- [ ] Symptom sufferers (experiencing but don't know cause)
- [ ] Self-diagnosers (researching their symptoms)
- [ ] Health optimizers (want to address early signs)

**Tone & Style:**
- Validating not alarmist
- Educational and empowering
- Science-backed explanations
- "Listen to your body" guidance

**CTA Guidelines:** Each CTA should:
- Reference symptom relief ("Stop Afternoon Crashes")
- Use mechanism language ("Support Healthy Levels")
- Include research backing ("Clinically Studied")
- Emphasize natural approach ("Safe, Effective")

**Conversion Tactics Use:**
- Symptom recognition validation
- Root cause deficiency explanations
- Clinical study references on ingredients
- Symptom improvement timelines
- Multi-symptom addressing (one solution helps several)
- Doctor validation quotes

**Key Elements to Include:**
- **Hook:** Relatable symptom moment that readers ignore (2-3 paragraphs)
- **List Items (6-9 symptoms):** Numbered symptom → root cause → your solution
- **Symptom Structure Per Item:**
  - Symptom headline: "#X: [Common symptom everyone experiences]"
  - Why this happens: Physiological root cause (deficiency, imbalance, etc.)
  - What your body is telling you: Biological signal explanation
  - Long-term risks: What happens if ignored
  - The solution: Your product with specific ingredient targeting
  - How it works: Ingredient mechanism of action
  - Research backing: Study references for ingredient efficacy
  - Expected improvement: Timeline for symptom relief
  - Customer quote: Someone who resolved this specific symptom
- **Product Callouts:** Active ingredient spotlights with "Address This" CTA
- **Multi-Symptom Connection:** How several symptoms link to one deficiency
- **Ingredient Breakdown:** Deep dive on key actives addressing symptoms (2-3 throughout)
- **Clinical Evidence:** Studies on ingredient effectiveness for symptoms
- **Dosage Information:** Therapeutic levels vs. dietary intake
- **Lab Testing Option:** "Test your levels" guidance if applicable
- **Complementary Approaches:** Lifestyle factors alongside supplementation
- **CTAs:** After symptom #2, after symptom #5, conclusion (5-7 total)
- **FAQ:** 4-5 questions (Safe daily? Works for all symptoms? How long? Side effects? Test needed first?)

**Goal:** Convert symptom-aware shoppers into proactive buyers by helping them recognize their body's signals, explaining the scientific root causes, and demonstrating how your targeted formula addresses the underlying deficiency or imbalance causing their symptoms.`,
  },
  {
    icon: <ClipboardList />,
    title: "Wellness Assessment Quizzes",
    description:
      "Build a quiz asking about health concerns and goals, then recommend 2-4 products with explanation of synergistic benefits.",
    prompt: `**Build a wellness assessment quiz that identifies health priorities and recommends personalized supplement stack with synergistic benefit explanations.**

**QUIZ FOCUS:** e.g., "What Does Your Body Actually Need?" OR "Find Your Wellness Stack" OR "Your Personalized Health Plan"

**PRODUCT RANGE:** e.g., Sleep, stress, immunity, digestion, energy (15-30 products to match from)

**Quiz Taker Type:** Choose one:
- [ ] Health goal seekers (specific outcomes wanted)
- [ ] Multi-symptom sufferers (several issues to address)
- [ ] Wellness optimizers (prevention-focused)

**Tone & Style:**
- Holistic health advisor
- Personalized and specific
- Science-backed recommendations
- Empowering self-care focus

**Quiz Question Guidelines:** Each question should:
- Focus on symptoms and lifestyle factors
- Use body-aware language
- Build toward root cause identification
- Address interconnected systems
- 7-9 total questions maximum

**Email Gate Strategy:**
- Place after question #5 (deep commitment)
- Frame as "Get your personalized wellness plan"
- Offer incentive: "Unlock plan + 25% off stack"
- Single field: email only
- Trust line: "Join 300,000+ on personalized wellness journeys"

**Results Page Elements:**
- **Personalized Headline:** "Your wellness focus: [Primary Health Area + Secondary Concerns]"
- **Health Profile Summary:** Top 2-3 areas needing support based on answers
- **Recommended Stack:** 2-4 products addressing identified concerns
- **Why This Stack:** How products work together synergistically
- **Product Details Per Item:** What it addresses, key ingredients, mechanism
- **Synergy Explanation:** How products enhance each other's effects
- **Priority Ranking:** Which to start with if budget-limited
- **Usage Protocol:** When to take each (morning/evening/with food)
- **Expected Timeline:** "Feel improvement in 2-4 weeks, optimal results 8-12 weeks"
- **Product Cards Include:** Image, health benefit, key actives, dosing, "Add to Stack"
- **Match Reasoning:** "You mentioned [symptom], so this [ingredient] supports that because..."
- **Stack Bundle:** Complete recommended set with bundle discount
- **Alternative Options:** Budget-friendly or single-focus alternatives
- **Lifestyle Recommendations:** Complementary habits for results
- **CTAs:** "Start Your Stack" and "Get Complete Bundle"
- **Retake Option:** "Health goals changed? Retake assessment"

**Conversion Tactics Use:**
- Holistic health approach (multiple systems)
- Synergistic benefit emphasis (better together)
- Stack bundle discount (15-25% off)
- Priority guidance (if can't afford all)
- Timeline expectations (patience = results)
- Personalized protocol framing

**Key Quiz Questions to Include:**
- **Primary Health Concern:** Sleep, stress, energy, immunity, digestion, focus, joint health
- **Energy Levels:** Throughout day pattern, crashes, fatigue
- **Sleep Quality:** Falling asleep, staying asleep, feeling rested
- **Stress Management:** Frequency, physical symptoms, coping ability
- **Digestive Health:** Regularity, bloating, discomfort, food sensitivities
- **Immune Function:** Frequency of illness, recovery time, seasonal issues
- **Lifestyle Factors:** Exercise, diet quality, work stress, screen time
- **Current Supplements:** What they're already taking (avoid conflicts)
- **Health Goals:** Prevention vs. treatment, specific outcomes wanted

**Goal:** Convert overwhelmed supplement shoppers into confident buyers by providing personalized multi-product recommendations that address their interconnected health concerns with clear explanations of how products work synergistically, increasing order value through strategic bundling.`,
  },
  {
    icon: <CircleDot />,
    title: "Demographic-Specific Quizzes",
    description:
      "Create age and gender-specific quizzes diagnosing health patterns, then provide personalized recommendations.",
    prompt: `**Build a demographic-specific quiz that addresses age and gender-related health patterns with personalized supplement recommendations.**

**DEMOGRAPHIC FOCUS:** e.g., "Women's Wellness Over 40" OR "Men's Health in Your 30s" OR "Teen Nutrition Support" OR "Senior Vitality Assessment"

**HEALTH PATTERNS:** e.g., Hormonal changes, metabolism shifts, energy decline, age-specific deficiencies

**Quiz Taker Type:** Choose one:
- [ ] Life stage transitioners (experiencing age-related changes)
- [ ] Preventative health seekers (want to age well)
- [ ] Hormone-aware (dealing with fluctuations)

**Tone & Style:**
- Age-appropriate and relatable
- Empathetic to life stage challenges
- Science-backed for credibility
- Empowering not defeatist about aging

**Quiz Question Guidelines:** Each question should:
- Address age/gender-specific symptoms
- Use life stage-appropriate language
- Build toward hormonal/metabolic patterns
- Acknowledge demographic health realities
- 7-9 total questions maximum

**Email Gate Strategy:**
- Place after question #5 (mid-commitment)
- Frame as "Get your [demographic] wellness plan"
- Offer incentive: "Unlock plan + 20% off [women's/men's/etc.] stack"
- Single field: email only
- Trust line: "Join 50,000+ [demographic] taking control of their health"

**Results Page Elements:**
- **Personalized Headline:** "[Demographic] health profile: [Key pattern identified]"
- **Life Stage Context:** What's happening in your body at this age/gender (2-3 paragraphs)
- **Pattern Identification:** Hormonal, metabolic, or deficiency patterns detected
- **Recommended Stack:** 2-4 products targeting demographic-specific needs
- **Why These For You:** How age/gender affects these specific needs
- **Product Details:** What each addresses for this demographic
- **Life Stage Benefits:** Beyond symptoms (bone health, hormone support, cognitive function)
- **Dosage Considerations:** Age/gender-appropriate amounts
- **Expected Timeline:** Realistic results for this demographic
- **Product Cards Include:** Image, demographic benefit, key nutrients, research, "Add to Cart"
- **Match Reasoning:** "At [age], [gender] typically experience [issue], so this helps..."
- **Bundle Option:** Complete [demographic] wellness stack with savings
- **Lifestyle Guidance:** Age-appropriate exercise, diet, stress management
- **Prevention Focus:** Future health concerns to address now
- **CTAs:** "Start Your [Demographic] Stack" and "Get Complete Bundle"
- **Retake Option:** "Life stage changed? Retake assessment"

**Conversion Tactics Use:**
- Age/gender validation ("This is normal at your stage")
- Peer social proof (others like you)
- Preventative health framing
- Research specific to demographic
- Life stage milestone targeting
- Community belonging emphasis

**Key Quiz Questions to Include:**

**For Women 40+:**
- Perimenopause/menopause symptoms
- Hot flashes, mood changes, sleep disruption
- Bone density concerns
- Metabolic changes
- Skin/hair health shifts

**For Men 30-50:**
- Energy and vitality levels
- Muscle mass maintenance
- Prostate health awareness
- Stress and performance
- Metabolic health

**For Seniors 60+:**
- Cognitive function
- Joint mobility
- Bone health
- Heart health
- Digestive efficiency

**Universal Questions:**
- Current health priorities
- Family health history
- Medication considerations
- Activity level for age
- Dietary patterns

**Goal:** Convert demographic-specific shoppers into confident buyers by addressing their unique age and gender-related health patterns with targeted recommendations that acknowledge biological realities while providing science-backed solutions for their life stage concerns.`,
  },
  {
    icon: <FlaskConical />,
    title: "Transparency & Lab Testing Pages",
    description:
      "Build a transparency page with lab reports viewable and certifications explained in plain language.",
    prompt: `**Build a transparency page that showcases lab testing, certifications, and quality standards in accessible language to build unshakeable trust.**

**TRANSPARENCY FOCUS:** e.g., "Our Testing Standards" OR "What's Inside: Complete Transparency" OR "Third-Party Verified Quality"

**CREDIBILITY ANGLE:** e.g., Independent lab testing, purity verification, potency guarantees

**Visitor Type:** Choose one:
- [ ] Quality-skeptics (burnt by supplement industry claims)
- [ ] Ingredient-conscious (research everything before buying)
- [ ] Health-serious (want pharmaceutical-grade standards)

**Tone & Style:**
- Radically transparent and honest
- Confident through proof not claims
- Educational without being technical
- "Nothing to hide" openness

**CTA Guidelines:** Each CTA should:
- Reference verification ("See The Proof")
- Use trust language ("Independently Tested")
- Include credential specifics ("Third-Party Certified")
- Emphasize quality ("Pharmaceutical-Grade")

**Conversion Tactics Use:**
- Downloadable lab reports (COAs)
- Certification badge explanations
- Comparison to industry standards
- Manufacturing facility credentials
- Batch-specific testing results
- Real-time test result access

**Key Elements to Include:**
- **Hero Section:** Bold transparency statement, "See Our Lab Results" CTA
- **Our Quality Promise:** What transparency means to you (2-3 paragraphs)
- **Testing Standards Overview:** What you test for and why
- **Third-Party Lab Partners:** Names, credentials, accreditations of testing facilities
- **Certificate of Analysis (COA) Access:**
  - How to find your batch number
  - Database of viewable COAs by product/batch
  - Sample COA with explanation of what each test means
- **What We Test For (8-12 categories):**
  - Potency verification (label claim accuracy)
  - Heavy metals (lead, mercury, arsenic, cadmium)
  - Microbial contamination
  - Pesticides and herbicides
  - Solvents and residuals
  - Allergen cross-contamination
  - Identity verification
  - Dissolution/disintegration
- **Certification Explanations:**
  - Each certification badge with plain-language explanation
  - What standards must be met
  - Who verifies
  - Why it matters
  - Re-certification frequency
- **Manufacturing Standards:**
  - GMP-certified facility details
  - FDA registration
  - NSF/cGMP certifications
  - Facility audit results
- **Sourcing Transparency:** Where ingredients come from, supplier standards
- **Batch Testing Process:** Every batch tested before release
- **Comparison Chart:** Our standards vs. industry minimum vs. competitors (5-7 rows)
- **Real Lab Reports:** 3-5 recent COAs prominently displayed with interpretation
- **Purity Guarantees:** What you guarantee and back with testing
- **Ingredient Traceability:** Supply chain transparency
- **Quality Control Process:** From sourcing to shipping
- **CTAs:** Hero, after COAs, with certifications, products section (5-6 total)
- **FAQ:** 6-8 questions (How to read COA? Test every batch? Third-party really? Failures ever? International standards? Access reports?)

**Goal:** Convert skeptical, quality-conscious consumers into loyal customers by providing radical transparency through accessible lab results, clear certification explanations, and pharmaceutical-grade standards that prove you have nothing to hide and everything to show.`,
  },
  {
    icon: <BarChart3 />,
    title: "Clinical Study Results",
    description:
      "Create a study-focused page with methodology, participant demographics, results graphs, and link to published research.",
    prompt: `**Build a clinical study results page that showcases research credibility through detailed methodology, visual data, and published research access.**

**STUDY FOCUS:** e.g., "Clinical Trial: [Product] for [Condition]" OR "Published Research on [Ingredient]" OR "Double-Blind Study Results"

**STUDY TYPE:** e.g., Randomized controlled trial, placebo-controlled, double-blind, peer-reviewed

**Visitor Type:** Choose one:
- [ ] Evidence-seekers (want proof before buying)
- [ ] Medically-minded (understand research)
- [ ] Skeptics (need peer-reviewed data)

**Tone & Style:**
- Scientific and credible
- Data-driven not marketing
- Accessible explanations of technical info
- Confident through evidence

**CTA Guidelines:** Each CTA should:
- Reference research backing ("Clinically Proven")
- Use scientific language ("Research-Backed")
- Include credibility markers ("Published Study")
- Link to products tested ("Shop Study Formula")

**Conversion Tactics Use:**
- Published research links
- Peer-review journal citations
- Visual data graphs/charts
- Statistical significance highlighted
- Before/after participant data
- Independent research facility credentials

**Key Elements to Include:**
- **Hero Section:** Study headline, key finding, "Read Full Study" + "Shop Study Formula" CTAs
- **Study Overview:** Quick summary of what was studied and main findings (2-3 paragraphs)
- **Key Results Highlight:** Top 3-5 statistically significant findings with percentages
- **Study Methodology:**
  - Study type (RCT, double-blind, etc.)
  - Duration (weeks/months)
  - Sample size and power
  - Inclusion/exclusion criteria
  - Control/placebo details
  - Dosage and protocol
  - Measurement methods
- **Participant Demographics:**
  - Age range
  - Gender breakdown
  - Health baseline characteristics
  - Geographic location
  - Completion rate
- **Results Data:**
  - Primary outcome measures with graphs
  - Secondary outcomes
  - Statistical significance (p-values)
  - Effect size
  - Time-to-result data
  - Adverse events (transparency)
- **Visual Data Presentation:**
  - Bar graphs comparing treatment vs. control
  - Line graphs showing improvement over time
  - Before/after comparison charts
  - Percentage improvement visuals
- **Statistical Analysis:** Methods used, confidence intervals, significance levels
- **Researcher Credentials:** Principal investigator bio, institution affiliation
- **Research Facility:** Lab/institution where conducted, accreditations
- **Peer Review Status:** Published in [Journal], peer-review process, impact factor
- **Full Study Access:** Link to published paper, DOI, abstract download
- **Limitations:** Honest discussion of study constraints (builds trust)
- **Product Connection:** Exact formulation tested, how to purchase
- **Additional Research:** Related studies supporting findings (3-5 references)
- **Expert Commentary:** Independent doctor/researcher analysis of results
- **CTAs:** Hero, after results, with product connection (4-5 total)
- **FAQ:** 5-7 questions (Who funded? Conflicts of interest? Replicable? All products studied? Dosage same? Side effects?)

**Goal:** Convert evidence-demanding shoppers into confident buyers by providing comprehensive clinical research transparency that proves efficacy through rigorous scientific methodology, peer-reviewed results, and accessible data visualization that builds credibility impossible to question.`,
  },
  {
    icon: <MessageCircle />,
    title: "Customer Testimonial Galleries",
    description:
      "Create a testimonial page organized by health concern with specific improvements and filtering options.",
    prompt: `**Build a customer testimonial gallery organized by health concern with detailed improvement stories and robust filtering for relatable social proof.**

**GALLERY FOCUS:** e.g., "Real Results from Real People" OR "Customer Health Transformations" OR "See What's Possible"

**CONCERN CATEGORIES:** e.g., Sleep, energy, pain relief, digestion, stress, immunity

**Gallery Visitor Type:** Choose one:
- [ ] Results-skeptics (need proof it works)
- [ ] Concern-specific (solving particular issue)
- [ ] Timeline-curious (how long until improvement)

**Tone & Style:**
- Authentic and unfiltered
- Celebratory of health wins
- Empathetic to struggles
- Results-focused not perfection-focused

**CTA Guidelines:** Each CTA should:
- Use transformation language ("Get Your Results")
- Reference specific concern ("Solve Sleep Issues")
- Include timeline context ("Feel Better in Weeks")
- Link to concern-specific products

**Conversion Tactics Use:**
- Filter by exact health concern match
- Timeline tags (2 weeks, 4 weeks, 3 months)
- Product used clearly displayed
- Verified purchase badges
- Testimonial submission incentive
- Specific metric improvements

**Key Elements to Include:**
- **Hero Section:** Headline, testimonial preview grid, filter options, "Submit Your Story" CTA
- **Filter Options:** By health concern, by product used, by timeline, by age/gender, verified only
- **Testimonial Grid:** Cards showing customer name, concern, improvement summary, timeline
- **Featured Stories (5-8 detailed):**
  - Customer name + photo (with permission)
  - Health concern addressed
  - Before condition (detailed symptoms)
  - Product(s) used
  - Timeline to improvement
  - Specific results (quantified when possible)
  - Life quality improvements
  - Full testimonial (200-300 words)
  - "Shop [Name]'s Solution" CTA
- **Concern-Specific Galleries:** Dedicated sections per health issue
- **Improvement Metrics Display:** "Average improvement: X weeks" per concern
- **Product Routines:** What each person took and how
- **Timeline Visualization:** Progress at weeks 2, 4, 8, 12
- **Verification Badges:** Confirmed purchase indicators
- **Age/Gender Tags:** For demographic relatability
- **Before/After Details:** Specific symptom changes with metrics
- **Social Proof Stats:** "15,000+ verified testimonials" or "92% report improvement"
- **Video Testimonials:** 3-5 video stories (60-90 sec each)
- **Submission Section:** "Share your story & get 25% off next order"
- **CTAs:** Top of page, with featured stories, concern sections (5-6 total)
- **FAQ:** 3-4 questions (How to submit? Privacy? Verification? Incentive details?)

**Goal:** Convert skeptical shoppers into confident buyers by showcasing authentic, concern-specific success stories with detailed improvement timelines that prove your products deliver real health results for people with similar issues and demographics.`,
  },
  {
    icon: <CalendarDays />,
    title: "Alternative Comparison Pages",
    description:
      "Create a comparison page positioning my product as the superior alternative, explaining competitor issues and showing ingredient differences.",
    prompt: `**Build an alternative comparison page that positions your product as the superior choice by exposing competitor shortcomings and highlighting ingredient superiority.**

**COMPARISON FOCUS:** e.g., "Why We're Better Than [Competitor]" OR "The Cleaner Alternative to [Product]" OR "[Product Type] Done Right"

**COMPARISON ANGLE:** e.g., Ingredient quality, dosage potency, bioavailability, additives, price-per-dose

**Visitor Type:** Choose one:
- [ ] Active comparers (researching between brands)
- [ ] Competitor customers (considering switch)
- [ ] Ingredient-conscious (scrutinize formulas)

**Tone & Style:**
- Factual and data-driven
- Confident not combative
- Educational about differences
- "We're not like them" positioning

**CTA Guidelines:** Each CTA should:
- Reference switching ("Make the Upgrade")
- Use superiority proof ("Get Higher Quality")
- Include science backing ("Better Bioavailability")
- Emphasize value ("Better Formula, Better Price")

**Conversion Tactics Use:**
- Side-by-side ingredient comparison
- Bioavailability/absorption differences
- Additive exposure (what they include, you don't)
- Dosage effectiveness analysis
- Price-per-effective-dose calculations
- Switcher testimonials with reasons

**Key Elements to Include:**
- **Hero Section:** Bold comparison headline, "Try the Better Alternative" CTA
- **Why People Switch:** Top 4-5 reasons customers leave competitors for you
- **Side-by-Side Comparison Chart (8-12 rows):**
  - Active ingredient form/quality
  - Dosage potency (mg per serving)
  - Bioavailability rate
  - Unnecessary additives
  - Allergens/fillers
  - Third-party testing
  - Certifications
  - Price per effective dose
  - Customer rating
- **Ingredient Quality Deep Dive:** Superior forms you use vs. inferior forms they use
- **Bioavailability Explanation:** Why absorption matters, how yours is better
- **What's NOT In Yours:** Harmful additives/fillers competitors use
- **Dosage Analysis:** Therapeutic levels vs. under-dosed formulas
- **Price Per Dose:** True value comparison showing yours is better value
- **Research Backing:** Studies on your ingredient forms vs. theirs
- **Third-Party Testing:** Your transparency vs. their lack of verification
- **Switcher Testimonials (6-8):** Former competitor customers explaining why they changed
- **Common Competitor Issues:** Problems customers report with their products
- **Your Superior Approach:** Manufacturing, sourcing, formulation philosophy
- **Money-Back Guarantee:** Risk-free trial for skeptical switchers
- **Sample Offer:** Low-commitment first purchase
- **Honest Acknowledgment:** If competitor has any advantage, address transparently
- **CTAs:** Hero, after comparison chart, with testimonials, guarantee section (5-6 total)
- **FAQ:** 4-5 questions (Really better? Worth switching? Taste different? Guarantee terms?)

**Goal:** Convert competitor customers into switchers by providing transparent, factual comparison that exposes ingredient quality differences, dosage inadequacies, and unnecessary additives while proving your superior formulation delivers better value and results backed by switcher testimonials.`,
  },
];
